import axios from "axios";
import { store } from "@/store";
import { logout } from "@/store/slices/authSlice";

const API_BASE_URL = import.meta.env.VITE_OPPORTUNITIES_API_URL || import.meta.env.VITE_CORE_API_URL;
const PIPELINE_API_BASE_URL = import.meta.env.VITE_PIPELINE_API_URL;
const RULES_API_BASE_URL = import.meta.env.VITE_RULES_API_URL;

// =========== Interfaces actualizadas ===========
export interface OpportunityItem {
  id: string;
  opportunity_id: string;
  product_id: string;
  quantity: number;
  base_price: number;
  applied_rules: AppliedRule[];
  line_total: string;
  updated_at: string;
  created_at: string;

  product?: {
    id: string;
    name: string;
    sku: string;
  };
}

export interface AppliedRule {
  rule_id: string;
  value: string;
}

export interface Collaborator {
  user_id: string;
}

export interface OpportunityTotals {
  subtotal: string;
  tax: string;
  total: string;
}

export interface AddItemWithRulesData {
  opportunity_id: string;
  product_id: string;
  quantity: number;
  base_price?: number;
  includes_installation?: boolean;
  slug?: string;
  user?: any;
  applied_rules?: { rule_id: string; value: string }[];
  notes?: string;
}

export interface AddItemWithoutRulesData {
  opportunity_id: string;
  product_id: string;
  quantity: number;
  base_price: number;
  includes_installation?: boolean;
  slug?: string;
  user?: any;
  notes?: string;
}

export interface AddItemResponseData {
  item: OpportunityItem;
  opportunity_totals: OpportunityTotals;
}

export interface AddItemResponse {
  success: boolean;
  message: string;
  data: AddItemResponseData;
}

// =========== NUEVAS INTERFACES PARA CONFIGURACIÓN DE ETAPAS ===========
export interface StageVisibilityConfig {
  value: number;
  unit: "hours" | "days" | "weeks" | "months";
}

export interface StageStagnationConfig {
  value: number;
  unit: "days" | "months" | "years";
  action: "notify" | "move_to_lost";
}

export interface StageAutoTransitionConfig {
  value: number;
  unit: "hours" | "days" | "months";
}

// Interfaces existentes (se mantienen)
export interface Opportunity {
  id: string;
  slug: string;
  name: string;
  pipeline_id?: string;
  client_company_id: string;
  contact_id: string;
  description?: string;
  estimated_value: string;
  currency_id: string;
  stage_id: string;
  estimated_close_date: string;
  opportunity_source_id: string;
  success_probability: number;
  notes: string;
  priority?: string;
  responsible_id?: string;
  lost_reason?: string;
  approver_name?: string;
  attachments: any[];
  created_by: string;
  stage_entered_at: string;
  last_activity_at: string;
  hidden_at?: string | null; // Fecha de ocultamiento automatico del kanban (null = visible)
  created_at: string;
  updated_at: string;
  is_archived?: boolean; // Para ocultar oportunidades en etapas 'closed'

  client_company?: {
    id: string;
    name: string;
  };
  contact?: {
    id: string;
    name: string;
    email: string;
  };
  currency?: {
    id: string;
    code: string;
    symbol: string;
  };
  stage?: {
    id: string;
    name: string;
    color: string;
    stage_type: string;
  };
  opportunity_source?: {
    id: string;
    name: string;
  };
  creator?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  collaborators?: Collaborator[];
  items?: OpportunityItem[]; // Agregado para incluir los items en la respuesta
  subtotal?: number; // Agregado para el total
  tax?: number; // Agregado para el impuesto
  total?: number; // Agregado para el total final

  version_number?: number;
  minor_version?: number;
  is_latest_version?: boolean;
}

// ======================================================
// INTERFACES para el sistema de Oportunidades (QuoteViewer)
// ======================================================

export interface QuoteItem {
  id: string;
  description: string;
  sku: string;
  quantity: number;
  unit_price: number;
  unit_price_formatted: string;
  original_price: number;
  original_price_formatted: string;
  has_deviation: boolean;
  line_total: number;
  line_total_formatted: string;
  includes_installation: boolean;
  includes_extras: boolean;
  applied_rules: any[];
}

export interface QuoteData {
  quote: {
    id: string;
    reference: string;
    name: string;
    version_number: number;
    minor_version: number;
    approval_status: string;
    date: string;
    validity_date: string;
    notes: string;
  };
  company: {
    name: string;
    tagline: string;
    address: string;
    phone: string;
    email: string;
    website: string;
  };
  client: {
    company_name: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
  };
  seller: {
    name: string;
    email: string;
  };
  currency: {
    symbol: string;
    code: string;
  };
  items: QuoteItem[];
  totals: {
    subtotal: number;
    subtotal_formatted: string;
    tax_rate: string;
    tax: number;
    tax_formatted: string;
    total: number;
    total_formatted: string;
  };
  terms: string[];
}

export interface OpportunityStage {
  id: string;
  slug: string;
  name: string;
  color: string;
  description?: string;
  order_position: number;
  stage_type: string;
  is_active: boolean;
  auto_transition_config?: StageAutoTransitionConfig | null;
  stagnation_config?: StageStagnationConfig | null;
  visibility_config?: StageVisibilityConfig | null; // Para ocultar etapas Ganada/Perdida
  created_at: string;
  updated_at: string;
  rules: OpportunityStageRule[];
}

export interface OpportunityStageRule {
  id: string;
  stage_id: string;
  rule_type: string;
  rule_config: {
    amount?: number;
    currency?: string;
    days?: number;
    error_message?: string;
  };
  action_type: string;
  action_config: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Pipeline {
  id: string;
  slug: string;
  name: string;
  description?: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  stages?: OpportunityStage[];
}

export interface CreateOpportunityData {
  slug: string;
  name: string;
  pipeline_id?: string;
  client_company_id: string;
  contact_id: string;
  estimated_value: number;
  currency_id: string;
  stage_id: string;
  estimated_close_date: string;
  opportunity_source_id: string;
  success_probability: number;
  notes: string;
  priority?: string;
  collaborators?: Collaborator[];
  created_by?: string;
  subtotal?: number;
  tax?: number;
  total?: number;
  description?: string;
}

// =========== NUEVAS INTERFACES PARA CREAR OPORTUNIDAD CON PRODUCTOS ===========
export interface CreateOpportunityItemData {
  product_id: string;
  quantity: number;
  applied_rules?: AppliedRule[];
}

export interface CreateOpportunityWithItemsData extends Omit<
  CreateOpportunityData,
  "subtotal" | "tax" | "total"
> {
  items: CreateOpportunityItemData[];
}

export interface UpdateOpportunityData extends Partial<CreateOpportunityData> {}

export interface MoveStageData {
  slug: string;
  stage_id: string;
  lost_reason?: string;
}

export interface CreateStageData {
  slug: string;
  name: string;
  color: string;
  order_position: number;
  stage_type: string;
  pipeline_id: string;
  is_active?: boolean;
  auto_transition_config?: StageAutoTransitionConfig | null;
  stagnation_config?: StageStagnationConfig | null;
  visibility_config?: StageVisibilityConfig | null;
}

export interface UpdateStageData extends Partial<CreateStageData> {}

export interface CreateStageRuleData {
  stage_id: string;
  rule_type: string;
  rule_config: {
    amount?: number;
    currency?: string;
    days?: number;
    error_message?: string;
  };
  action_type: string;
  action_config: any;
  is_active?: boolean;
}

export interface UpdateStageRuleData extends Partial<CreateStageRuleData> {}

// =========== Interfaces para reasignación ===========
export interface ReassignOpportunityData {
  slug: string;
  new_owner_id: string;
}

export interface BulkReassignData {
  slug: string;
  current_owner_id: string;
  new_owner_id: string;
}

export interface BulkReassignResponse {
  success: boolean;
  message: string;
  data: {
    count: number;
    previous_owner_id: string;
    new_owner_id: string;
  };
}

// =========== Interfaces para agregar items ===========
export interface AddItemWithRulesData {
  opportunity_id: string;
  product_id: string;
  quantity: number;
  applied_rules?: AppliedRule[];
}

export interface AddItemWithoutRulesData {
  opportunity_id: string;
  product_id: string;
  quantity: number;
}

export interface OpportunitiesResponse {
  success: boolean;
  data: Opportunity[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface OpportunityResponse {
  success: boolean;
  data: Opportunity;
}

export interface StagesResponse {
  success: boolean;
  data: OpportunityStage[];
  count: number;
}

export interface StageResponse {
  success: true;
  data: OpportunityStage;
}

export interface StageRuleResponse {
  success: boolean;
  data: OpportunityStageRule;
}

export interface QueryParams {
  slug: string;
  page?: number;
  limit?: number;
  search?: string;
  client_company_id?: string;
  stage_id?: string;
  opportunity_source_id?: string;
  from_date?: string;
  to_date?: string;
  user_id?: string;
  exclude_closed?: boolean;
  include_hidden?: boolean; // true: incluye oportunidades ocultas (con hidden_at != null)
  only_hidden?: boolean; // true: solo oportunidades ocultas (historial)
  ids?: string[];
}

export interface StageQueryParams {
  slug: string;
  is_active?: boolean;
  pipeline_id?: string;
}

export interface PipelineQueryParams {
  slug: string;
  role_id?: string;
  is_active?: boolean;
}

export interface PipelinesResponse {
  success: boolean;
  data: Pipeline[];
  count: number;
}

export interface PipelineResponse {
  success: boolean;
  data: Pipeline;
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const pipelineApi = axios.create({
  baseURL: PIPELINE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const rulesApi = axios.create({
  baseURL: RULES_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    const publicEndpoints = ["/login"];
    const isPublicEndpoint = publicEndpoints.some((endpoint) =>
      config.url?.endsWith(endpoint),
    );

    if (token && !isPublicEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      (response.data.statusCode === 200 || response.data.statusCode === 201) &&
      response.data.body
    ) {
      response.data = response.data.body;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.hash = "#/login";
    }

    if (error.response?.data?.body?.message) {
      error.message = error.response.data.body.message;
    }

    return Promise.reject(error);
  },
);

pipelineApi.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    const publicEndpoints = ["/login"];
    const isPublicEndpoint = publicEndpoints.some((endpoint) =>
      config.url?.endsWith(endpoint),
    );

    if (token && !isPublicEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

pipelineApi.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      (response.data.statusCode === 200 || response.data.statusCode === 201) &&
      response.data.body
    ) {
      response.data = response.data.body;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.hash = "#/login";
    }

    if (error.response?.data?.body?.message) {
      error.message = error.response.data.body.message;
    }

    return Promise.reject(error);
  },
);

rulesApi.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    const publicEndpoints = ["/login"];
    const isPublicEndpoint = publicEndpoints.some((endpoint) =>
      config.url?.endsWith(endpoint),
    );

    if (token && !isPublicEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

rulesApi.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      (response.data.statusCode === 200 || response.data.statusCode === 201) &&
      response.data.body
    ) {
      response.data = response.data.body;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.hash = "#/login";
    }

    if (error.response?.data?.body?.message) {
      error.message = error.response.data.body.message;
    }

    return Promise.reject(error);
  },
);

export const opportunityAPI = {
  // =========== Métodos existentes (se mantienen) ===========
  getOpportunities: async (
    params: QueryParams,
  ): Promise<OpportunitiesResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("slug", params.slug);

      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.search) queryParams.append("search", params.search);
      if (params.client_company_id)
        queryParams.append("client_company_id", params.client_company_id);
      if (params.stage_id) queryParams.append("stage_id", params.stage_id);
      if (params.opportunity_source_id)
        queryParams.append(
          "opportunity_source_id",
          params.opportunity_source_id,
        );
      if (params.from_date) queryParams.append("from_date", params.from_date);
      if (params.to_date) queryParams.append("to_date", params.to_date);
      if (params.user_id)
        queryParams.append("user_id", params.user_id.toString());
      if (params.exclude_closed !== undefined)
        queryParams.append("exclude_closed", params.exclude_closed.toString());
      if (params.include_hidden !== undefined)
        queryParams.append("include_hidden", params.include_hidden.toString());
      if (params.only_hidden !== undefined)
        queryParams.append("only_hidden", params.only_hidden.toString());

      const response = await api.get(
        `/opportunities?${queryParams.toString()}`,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          pagination: response.data.pagination,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al obtener oportunidades",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  exportOpportunities: async (
    params: QueryParams,
  ): Promise<OpportunitiesResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("slug", params.slug);

      if (params.search) queryParams.append("search", params.search);
      if (params.client_company_id)
        queryParams.append("client_company_id", params.client_company_id);
      if (params.stage_id) queryParams.append("stage_id", params.stage_id);
      if (params.opportunity_source_id)
        queryParams.append(
          "opportunity_source_id",
          params.opportunity_source_id,
        );
      if (params.from_date) queryParams.append("from_date", params.from_date);
      if (params.to_date) queryParams.append("to_date", params.to_date);
      if (params.user_id)
        queryParams.append("user_id", params.user_id.toString());
      if (params.exclude_closed !== undefined)
        queryParams.append("exclude_closed", params.exclude_closed.toString());
      if (params.include_hidden !== undefined)
        queryParams.append("include_hidden", params.include_hidden.toString());
      if (params.only_hidden !== undefined)
        queryParams.append("only_hidden", params.only_hidden.toString());
      if (params.ids && params.ids.length > 0)
        queryParams.append("ids", JSON.stringify(params.ids));

      const response = await api.get(
        `/opportunities/export?${queryParams.toString()}`,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          pagination: response.data.pagination,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al exportar oportunidades",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  getOpportunityById: async (
    id: string,
    slug: string,
  ): Promise<OpportunityResponse> => {
    try {
      const response = await api.get(`/opportunities/${id}?slug=${slug}`);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al obtener la oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  // =========== MÉTODO NUEVO para crear oportunidad con productos ===========
  /**
   * Crea una oportunidad con productos en una sola petición
   */
  createOpportunityWithItems: async (
    opportunityData: CreateOpportunityWithItemsData,
  ): Promise<OpportunityResponse> => {
    try {
      const response = await api.post(`/opportunities`, opportunityData);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message ||
            "Error al crear la oportunidad con productos",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  createOpportunity: async (
    opportunityData: CreateOpportunityData,
  ): Promise<OpportunityResponse> => {
    try {
      const response = await api.post(`/opportunities`, opportunityData);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al crear la oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  updateOpportunity: async (
    id: string,
    opportunityData: UpdateOpportunityData,
  ): Promise<OpportunityResponse> => {
    try {
      const { slug, ...updateData } = opportunityData;

      if (!slug) {
        throw new Error("El slug es requerido para actualizar la oportunidad");
      }

      const response = await api.put(
        `/opportunities/${id}?slug=${slug}`,
        updateData,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al actualizar la oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  moveOpportunityStage: async (
    id: string,
    moveData: MoveStageData,
  ): Promise<OpportunityResponse> => {
    try {
      const response = await api.post(
        `/opportunities/${id}/move-stage`,
        moveData,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al mover la etapa de la oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  deleteOpportunity: async (
    id: string,
    slug: string,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete(`/opportunities/${id}?slug=${slug}`);

      if (response.data.success) {
        return {
          success: true,
          message:
            response.data.message || "Oportunidad eliminada exitosamente",
        };
      } else {
        throw new Error(
          response.data?.message || "Error al eliminar la oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  // =========== NUEVOS MÉTODOS PARA MANEJAR ITEMS ===========

  /**
   * Agrega un producto a una oportunidad con reglas aplicadas
   */
  addItemWithRules: async (
    itemData: AddItemWithRulesData,
  ): Promise<AddItemResponse> => {
    try {
      const response = await api.post(`/opportunities/items`, itemData);

      if (response.data.success) {
        return {
          success: true,
          message:
            response.data.message ||
            "Producto agregado a oportunidad exitosamente",
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message ||
            "Error al agregar producto a la oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Agrega un producto a una oportunidad sin reglas aplicadas
   */
  addItemWithoutRules: async (
    itemData: AddItemWithoutRulesData,
  ): Promise<AddItemResponse> => {
    try {
      const response = await api.post(`/opportunities/items`, itemData);

      if (response.data.success) {
        return {
          success: true,
          message:
            response.data.message ||
            "Producto agregado a oportunidad exitosamente",
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message ||
            "Error al agregar producto a la oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Elimina un producto (item) de una oportunidad
   */
  deleteItem: async (
    itemId: string,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete(`/opportunities/items/${itemId}`);

      if (response.data.success) {
        return {
          success: true,
          message:
            response.data.message ||
            "Producto eliminado de la oportunidad exitosamente",
        };
      } else {
        throw new Error(
          response.data?.message ||
            "Error al eliminar producto de la oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Reasigna una oportunidad individual a otro usuario
   */
  reassignOpportunity: async (
    id: string,
    data: ReassignOpportunityData,
  ): Promise<OpportunityResponse> => {
    try {
      const response = await api.put(`/opportunities/${id}/reassign`, data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al reasignar la oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Reasignación masiva de oportunidades de un vendedor a otro
   */
  bulkReassignOpportunities: async (
    data: BulkReassignData,
  ): Promise<BulkReassignResponse> => {
    try {
      const response = await api.post(`/opportunities/bulk-reassign`, data);

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error en la reasignación masiva",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  getPipelines: async (
    params: PipelineQueryParams,
  ): Promise<PipelinesResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("slug", params.slug);

      if (params.role_id) {
        queryParams.append("role_id", params.role_id);
      }

      if (params.is_active !== undefined) {
        const isActiveValue = params.is_active ? 1 : 0;
        queryParams.append("is_active", isActiveValue.toString());
      }

      const response = await pipelineApi.get(
        `/pipelines?${queryParams.toString()}`,
      );

      if (response.data.success) {
        const pipelinesData = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        return {
          success: true,
          data: pipelinesData,
          count: response.data.count || pipelinesData.length,
        };
      } else {
        throw new Error(response.data?.message || "Error al obtener pipelines");
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  getPipelineById: async (
    id: string,
    slug: string,
  ): Promise<PipelineResponse> => {
    try {
      const response = await pipelineApi.get(`/pipelines/${id}?slug=${slug}`);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al obtener el pipeline",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  // =========== Métodos existentes para stages (se mantienen) ===========
  getStages: async (params: StageQueryParams): Promise<StagesResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("slug", params.slug);

      if (params.is_active !== undefined) {
        const isActiveValue = params.is_active ? 1 : 0;
        queryParams.append("is_active", isActiveValue.toString());
      }

      if (params.pipeline_id) {
        queryParams.append("pipeline_id", params.pipeline_id);
      }

      const response = await api.get(
        `/opportunity-stages?${queryParams.toString()}`,
      );

      if (response.data.success) {
        const stagesData = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        const sortedData = stagesData.sort(
          (a: OpportunityStage, b: OpportunityStage) => {
            return a.order_position - b.order_position;
          },
        );

        return {
          success: true,
          data: sortedData,
          count: response.data.count || sortedData.length,
        };
      } else {
        throw new Error(response.data?.message || "Error al obtener etapas");
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  createStage: async (stageData: CreateStageData): Promise<StageResponse> => {
    try {
      const dataToSend = {
        ...stageData,
        is_active: stageData.is_active ?? true,
        stage_type: stageData.stage_type || "progress",
      };

      const response = await api.post(`/opportunity-stages`, dataToSend);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(response.data?.message || "Error al crear la etapa");
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  updateStage: async (
    id: string,
    stageData: UpdateStageData,
  ): Promise<StageResponse> => {
    try {
      if (!stageData.slug) {
        throw new Error("El slug es requerido para actualizar la etapa");
      }

      const { slug, ...updateData } = stageData;

      const response = await api.put(
        `/opportunity-stages/${id}?slug=${slug}`,
        updateData,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al actualizar la etapa",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  deleteStage: async (
    id: string,
    slug: string,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete(
        `/opportunity-stages/${id}?slug=${slug}`,
      );

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || "Etapa eliminada exitosamente",
        };
      } else {
        throw new Error(response.data?.message || "Error al eliminar la etapa");
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  createStageRule: async (
    ruleData: CreateStageRuleData,
    slug: string,
  ): Promise<StageRuleResponse> => {
    try {
      const dataToSend = {
        ...ruleData,
        slug: slug,
      };

      const response = await rulesApi.post(
        `/opportunity-stages/rules`,
        dataToSend,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(response.data?.message || "Error al crear la regla");
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  updateStageRule: async (
    id: string,
    ruleData: UpdateStageRuleData,
    slug: string,
  ): Promise<StageRuleResponse> => {
    try {
      const dataToSend = {
        ...ruleData,
        slug: slug,
      };

      const response = await rulesApi.put(
        `/opportunity-stages/rules/${id}`,
        dataToSend,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al actualizar la regla",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  deleteStageRule: async (
    id: string,
    slug: string,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await rulesApi.delete(
        `/opportunity-stages/rules/${id}?slug=${slug}`,
      );

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || "Regla eliminada exitosamente",
        };
      } else {
        throw new Error(response.data?.message || "Error al eliminar la regla");
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  getVersions: async (id: string, slug: string): Promise<{ success: boolean; data: Opportunity[] }> => {
    try {
      const response = await api.get(`/opportunities/${id}/versions?slug=${slug}`);
      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      throw new Error(response.data?.message || "Error al obtener versiones");
    } catch (error: any) {
      if (error.response) throw new Error(error.response.data?.message || "Error en el servidor");
      throw new Error("Error al configurar la solicitud");
    }
  },

  createVersion: async (id: string, slug: string): Promise<{ success: boolean; data: Opportunity }> => {
    try {
      const response = await api.post(`/opportunities/${id}/clone`, { slug });
      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      throw new Error(response.data?.message || "Error al crear la versión");
    } catch (error: any) {
      if (error.response) throw new Error(error.response.data?.message || "Error en el servidor");
      throw new Error("Error al configurar la solicitud");
    }
  },

  // Métodos de Cotización
  previewQuote: async (id: string, slug: string): Promise<Blob> => {
    try {
      const response = await api.get(`/opportunities/${id}/preview-pdf?slug=${slug}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error al obtener vista previa de cotización");
    }
  },

  getQuoteData: async (id: string, slug: string): Promise<QuoteData> => {
    try {
      const response = await api.get(`/opportunities/${id}/quote-data?slug=${slug}`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || "Error al obtener datos de la oportunidad");
    }
  },

  getOpportunityVersions: async (id: string, slug: string): Promise<{ success: boolean; data: Opportunity[] }> => {
    return opportunityAPI.getVersions(id, slug);
  },

  cloneOpportunity: async (id: string, slug: string): Promise<{ success: boolean; data: Opportunity }> => {
    return opportunityAPI.createVersion(id, slug);
  },

  sendQuoteEmail: async (id: string, slug: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.post(`/opportunities/${id}/send-quote`, { slug });
      if (response.data.success) {
        return { success: true, message: response.data.message };
      }
      throw new Error(response.data?.message || "Error al enviar la cotización");
    } catch (error: any) {
      if (error.response) throw new Error(error.response.data?.message || "Error en el servidor");
      throw new Error("Error al configurar la solicitud");
    }
  },

  uploadAttachment: async (id: string, slug: string, file: File, description: string): Promise<{ success: boolean; message: string; data?: any }> => {
    try {
      // Step 1: Get a presigned upload URL from the backend
      const urlResponse = await api.post(`/opportunities/${id}/upload-url`, {
        slug,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      });

      if (!urlResponse.data?.success || !urlResponse.data?.data?.upload_url) {
        throw new Error(urlResponse.data?.message || 'No se obtuvo URL de subida');
      }

      const { upload_url, file_url } = urlResponse.data.data;

      // Step 2: Upload the file directly to S3 (bypasses CORS/size restrictions)
      await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });

      // Extraer la S3 key del file_url o upload_url para que el backend pueda firmarla en el get
      const s3KeyMatch = file_url.match(/amazonaws\.com\/(.+)$/);
      const s3Key = s3KeyMatch ? decodeURIComponent(s3KeyMatch[1]) : "";

      // Step 3: Notify backend that file was uploaded and save attachment metadata
      const attachResponse = await api.post(`/opportunities/${id}/attachments?slug=${slug}`, {
        slug,
        attachment_url: file_url,
        attachment_name: file.name,
        attachment_type: file.type || 'application/octet-stream',
        attachment_size: file.size,
        attachment_key: s3Key,
        description: description || 'Archivo adjunto',
      });

      if (attachResponse.data?.success) {
        return { success: true, message: attachResponse.data.message, data: attachResponse.data.data };
      }
      throw new Error(attachResponse.data?.message || 'Error al registrar el adjunto');
    } catch (error: any) {
      if (error.response) throw new Error(error.response.data?.message || 'Error en el servidor');
      throw new Error(error.message || 'Error al subir el archivo');
    }
  },

  formalizeSale: async (id: string, slug: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.post(`/opportunities/${id}/formalize`, { slug });
      if (response.data.success) {
        return { success: true, message: response.data.message };
      }
      throw new Error(response.data?.message || "Error al formalizar la venta");
    } catch (error: any) {
      if (error.response) throw new Error(error.response.data?.message || "Error en el servidor");
      throw new Error("Error al configurar la solicitud");
    }
  },
};

api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      response.data.statusCode === 200 &&
      response.data.body
    ) {
      response.data = response.data.body.data || response.data.body;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.hash = "#/session-expired";
    }
    return Promise.reject(error);
  },
);
